import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { Copy, ExternalLink, CheckCircle, Key, Database } from 'lucide-react';
import { BrandIcons } from '../../ui/brand-icons';
import { toast } from 'sonner';

export interface DifySuccessIntegration {
  integration_name: string;
  binding_id: string;
  auth_config: {
    api_key: string;
  };
  space_id: string;
  permissions: {
    can_read?: boolean;
    can_write?: boolean;
    can_search?: boolean;
    can_delete?: boolean;
    rate_limits?: { requests_per_hour?: number };
  };
}

export interface DifySuccessModalContentProps {
  onClose: () => void;
  integration: DifySuccessIntegration;
}

export const DifySuccessModalContent: React.FC<DifySuccessModalContentProps> = ({
  onClose,
  integration,
}) => {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL || 'https://api.memara.io';
  const difyApiPrefix = `${apiBaseUrl.replace(/\/$/, '')}/api/v1/dify`;

  const handleCopy = async (text: string, item: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item);
      toast.success(`${item} copied to clipboard`);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
        <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          Dify integration ready
        </DialogTitle>
      </DialogHeader>

      <DialogScrollArea className="space-y-6 px-6">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-green-900">
                  {integration.integration_name}
                </h3>
                <p className="text-green-700 text-sm">
                  Binding ID: {integration.binding_id}
                </p>
              </div>
              <BrandIcons.dify className="w-12 h-12 shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Memara credentials for Dify
            </CardTitle>
            <p className="text-sm text-gray-600">
              In the Memara Dify plugin, set <strong>API base URL</strong> and paste
              your <strong>API key</strong> (Bearer token). Tools call{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">/api/v1/dify/…</code>{' '}
              on this host.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API base URL
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-50 border rounded-md px-3 py-2 text-sm font-mono break-all">
                  {apiBaseUrl}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => handleCopy(apiBaseUrl, 'API base URL')}
                >
                  {copiedItem === 'API base URL' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dify API prefix (for reference)
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-50 border rounded-md px-3 py-2 text-sm font-mono break-all">
                  {difyApiPrefix}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => handleCopy(difyApiPrefix, 'Dify API prefix')}
                >
                  {copiedItem === 'Dify API prefix' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API key
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-50 border rounded-md px-3 py-2 text-sm font-mono break-all">
                  {showApiKey
                    ? integration.auth_config.api_key
                    : '•'.repeat(Math.min(32, integration.auth_config.api_key.length))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() =>
                    handleCopy(integration.auth_config.api_key, 'API key')
                  }
                >
                  {copiedItem === 'API key' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-indigo-600" />
              Next steps
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-700 space-y-2">
            <ol className="list-decimal list-inside space-y-1">
              <li>Install the Memara plugin package in your Dify workspace.</li>
              <li>Add a Memara tool provider using the URL and API key above.</li>
              <li>
                Use tools (create, search, list, get, update, delete) or schedule
                polling with List/Search and workflow variables.
              </li>
            </ol>
            <p className="text-xs text-gray-500 pt-2">
              See the plugin README for limits (e.g. list max 100, search max 50
              results per request).
            </p>
          </CardContent>
        </Card>

      </DialogScrollArea>
      <DialogFooter variant="scrollable" className="flex-wrap gap-3 px-6 pb-6">
        <Button onClick={onClose} className="bg-indigo-600 hover:bg-indigo-700">
          Done
        </Button>
        <Button variant="outline" asChild>
          <a
            href="https://docs.dify.ai/en/develop-plugin/dev-guides-and-walkthroughs/tool-plugin"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Dify tool plugins
          </a>
        </Button>
      </DialogFooter>
    </div>
  );
};

interface DifySuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: DifySuccessIntegration;
}

export const DifySuccessModal: React.FC<DifySuccessModalProps> = ({
  isOpen,
  onClose,
  integration,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent variant="scrollable" className="max-w-2xl bg-white">
        <DifySuccessModalContent integration={integration} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};
